/**
 * Preferred Creditors Panel — data table with stat cards, category filter,
 * entitlement columns, totals row, and detail slide-out.
 *
 * Reads canonical employee entries from the `employeeEntries` table.
 * Updates incrementally as the agent writes entries.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Loader2, Users, Plus, ChevronRight } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  EMPLOYMENT_BASIS_LABELS,
  sumEmployeeEntitlements,
  sumFegRecoverable,
} from "@cvx/lib/employeeEntitlements";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import { findContactForSource } from "@/components/jobs/phases/contacts/find-contact-for-source";
import { RelatedPartyBadge } from "../../../_shared/RelatedPartyBadge";
import type { RelatedPartyMatchUI } from "../../../_shared/relatedPartyShared";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../_shared/new-output-badge";
import type { EmployeeEntryUI } from "./types";
import { EmployeeDetailPanel } from "./EmployeeDetailPanel";
import { FegExportButton } from "./FegExportButton";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CATEGORY_STYLES: Record<
  string,
  { label: string; abbr: string; color: BadgeColor }
> = {
  FT: { label: "Full-time", abbr: "FT", color: "blue" },
  PT: { label: "Part-time", abbr: "PT", color: "emerald" },
  Casual: { label: "Casual", abbr: "Cas", color: "amber" },
  Shiftwork: { label: "Shiftwork", abbr: "Shift", color: "violet" },
  Probation: { label: "Probation", abbr: "Prob", color: "sky" },
  Other: { label: "Other", abbr: "Other", color: "gray" },
};

function fmtCurrency(n?: number): string {
  if (n == null || n === 0) return "\u2014";
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fmtDate(d?: string): string {
  if (!d) return "\u2014";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
}

function totalEntitlements(e: EmployeeEntryUI): number {
  return sumEmployeeEntitlements(e.entitlements);
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/30 px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 text-2xl font-semibold tabular-nums",
          accent ?? "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EmployeeEntitlementsPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the stat-cards row (Employees / Total
   * entitlements / Unpaid wages / Unpaid super) and the "Add Employee"
   * button so the panel renders as table-only. Used by the Overview
   * (Plan) page.
   */
  compact?: boolean;
}) {
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);
  // Excluded employees stay in the register but are dropped from every
  // preferred-creditor total, the waterfall, POD and reporting. Hidden from the
  // main list; the "Excluded" view toggle reveals them (mirrors Assets).
  const [showExcluded, setShowExcluded] = useState(false);

  const addEmployee = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .addManualEmployee,
    ),
  });

  const newSince = useNewOutputBoundary(jobId, "sum_employment");

  const { data: allEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listEmployeeEntriesForJob,
      { jobId, includesMerged: true },
    ),
  );

  // The FEG service calculations and the export header both need the
  // case-level insolvency dates and the entity name.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );

  const { data: jobContacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );

  // A director or a director's relative is an excluded employee, so the flag
  // belongs on the register itself rather than only inside the detail panel.
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
  );

  const relatedByEntry = useMemo(() => {
    const map = new Map<string, RelatedPartyMatchUI>();
    for (const { entryId, ...match } of relatedFlags?.employees ?? []) {
      map.set(entryId, match);
    }
    return map;
  }, [relatedFlags]);

  const entries = useMemo(() => allEntries ?? [], [allEntries]);

  const contactIdByEntry = useMemo(() => {
    const map = new Map<string, Id<"jobContacts">>();
    for (const entry of entries) {
      const contactId = findContactForSource(
        jobContacts ?? [],
        "employee",
        entry._id,
        entry.name,
      );
      if (contactId) map.set(entry._id, contactId);
    }
    return map;
  }, [entries, jobContacts]);

  const activeEntries = useMemo(
    () => entries.filter((e) => e.status === "active"),
    [entries],
  );

  const nonExcludedActive = useMemo(
    () => activeEntries.filter((e) => !e.isExcluded),
    [activeEntries],
  );
  const excludedActive = useMemo(
    () => activeEntries.filter((e) => e.isExcluded),
    [activeEntries],
  );
  // What the table works from: the active register by default, or the excluded
  // rows when the practitioner switches to the Excluded view.
  const baseEntries = showExcluded ? excludedActive : nonExcludedActive;

  // Stats — always exclude the excluded rows.
  const stats = useMemo(() => {
    const totals = nonExcludedActive.reduce(
      (acc, e) => ({
        totalEnt: acc.totalEnt + totalEntitlements(e),
        fegRecoverable: acc.fegRecoverable + sumFegRecoverable(e.entitlements),
        wages: acc.wages + (e.entitlements?.unpaidWages ?? 0),
        superAmt: acc.superAmt + (e.entitlements?.unpaidSuper ?? 0),
      }),
      { totalEnt: 0, fegRecoverable: 0, wages: 0, superAmt: 0 },
    );
    return {
      totalEmployees: nonExcludedActive.length,
      totalEntitlements: totals.totalEnt,
      totalFegRecoverable: totals.fegRecoverable,
      totalWages: totals.wages,
      totalSuper: totals.superAmt,
    };
  }, [nonExcludedActive]);

  // Filtered
  const filtered = useMemo(() => {
    return (
      categoryFilter !== "all"
        ? baseEntries.filter((e) => e.employmentCategory === categoryFilter)
        : baseEntries
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [baseEntries, categoryFilter]);

  // Column totals for filtered
  const totals = useMemo(() => {
    const t = {
      wages: 0,
      super: 0,
      annual: 0,
      loading: 0,
      lsl: 0,
      pilon: 0,
      redundancy: 0,
      total: 0,
    };
    filtered.forEach((e) => {
      // Excluded employees never count towards the totals (they show $0 in the
      // Excluded view, matching the Assets pattern).
      if (e.isExcluded) return;
      t.wages += e.entitlements?.unpaidWages ?? 0;
      t.super += e.entitlements?.unpaidSuper ?? 0;
      t.annual += e.entitlements?.accruedAnnualLeave ?? 0;
      t.loading += e.entitlements?.leaveLoading ?? 0;
      t.lsl += e.entitlements?.accruedLongServiceLeave ?? 0;
      t.pilon += e.entitlements?.paymentInLieuOfNotice ?? 0;
      t.redundancy += e.entitlements?.redundancyPay ?? 0;
      t.total += totalEntitlements(e);
    });
    return t;
  }, [filtered]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  // ---------------------------------------------------------------------------
  // Empty / loading states
  // ---------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting employees...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <Users className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No employee entries yet.
        </p>
        {!compact && !isViewer && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2 gap-1.5"
            disabled={addEmployee.isPending}
            onClick={() => {
              addEmployee.mutate(
                { jobId, taskId, name: "New Employee" },
                { onSuccess: (id) => setSelectedEntryId(id as string) },
              );
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Employee
          </Button>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Stats — hidden in compact mode */}
        {!compact && (
          <div className="grid grid-cols-5 gap-3 px-5 pb-4 pt-5">
            <StatCard label="Employees" value={stats.totalEmployees} />
            <StatCard
              label="Total entitlements"
              value={fmtCurrency(stats.totalEntitlements)}
            />
            <StatCard
              label="Est FEG Entitlement"
              value={fmtCurrency(stats.totalFegRecoverable)}
            />
            <StatCard
              label="Unpaid wages"
              value={fmtCurrency(stats.totalWages)}
            />
            <StatCard
              label="Unpaid super"
              value={fmtCurrency(stats.totalSuper)}
            />
          </div>
        )}

        {/* Filter + Add — Add button hidden in compact mode */}
        <div
          className={cn(
            "flex items-center justify-between px-5 pb-4",
            compact && "pt-5",
          )}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 items-center rounded-md border border-input p-0.5">
              <button
                type="button"
                onClick={() => setShowExcluded(false)}
                className={cn(
                  "h-7 rounded px-2.5 text-xs font-medium transition-colors",
                  !showExcluded
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setShowExcluded(true)}
                className={cn(
                  "h-7 rounded px-2.5 text-xs font-medium transition-colors",
                  showExcluded
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Excluded
                {excludedActive.length > 0 ? ` (${excludedActive.length})` : ""}
              </button>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 w-[190px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bases of employment</SelectItem>
                {Object.entries(EMPLOYMENT_BASIS_LABELS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          {!compact && (
            <div className="flex items-center gap-2">
              <FegExportButton entries={nonExcludedActive} job={job ?? null} />
              {!isViewer && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={addEmployee.isPending}
                  onClick={() => {
                    addEmployee.mutate(
                      { jobId, taskId, name: "New Employee" },
                      {
                        onSuccess: (id) => {
                          setSelectedEntryId(id as string);
                        },
                      },
                    );
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Employee
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Data table */}
        <div className="border-t border-border">
          <div className="min-w-0 flex-1">
            <div className="overflow-auto">
              <Table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[15%]" />
                  <col className="w-[6%]" />
                  <col className="w-[7%]" />
                  <col className="w-[7%]" />
                  <col className="w-[9%]" />
                  <col className="w-[8%]" />
                  <col className="w-[8%]" />
                  <col className="w-[8%]" />
                  <col className="w-[7%]" />
                  <col className="w-[8%]" />
                  <col className="w-[9%]" />
                  <col className="w-[4%]" />
                </colgroup>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-transparent">
                    <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Name
                    </TableHead>
                    <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Basis
                    </TableHead>
                    <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Start
                    </TableHead>
                    <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      End
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Wages
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Super
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Ann. lv
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Lv loading
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      LSL
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      PILON
                    </TableHead>
                    <TableHead className="h-10 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Total
                    </TableHead>
                    <TableHead className="h-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="h-32 text-center">
                        <span className="text-sm text-muted-foreground">
                          No employees match this filter.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((emp) => {
                      const cat = CATEGORY_STYLES[emp.employmentCategory ?? ""];
                      const total = totalEntitlements(emp);
                      return (
                        <TableRow
                          key={emp._id}
                          onClick={() => setSelectedEntryId(emp._id)}
                          className={cn(
                            "cursor-pointer transition-colors",
                            selectedEntryId === emp._id
                              ? "bg-primary/5"
                              : "hover:bg-muted/50",
                          )}
                        >
                          <TableCell className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <button
                                type="button"
                                className={cn(
                                  "truncate text-left text-sm font-medium underline-offset-2 hover:underline",
                                  emp.isExcluded
                                    ? "text-muted-foreground line-through"
                                    : contactIdByEntry.has(emp._id)
                                      ? "text-primary"
                                      : "text-foreground",
                                )}
                                title={
                                  contactIdByEntry.has(emp._id)
                                    ? `Open ${emp.name}'s contact card`
                                    : emp.name
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                  const contactId = contactIdByEntry.get(
                                    emp._id,
                                  );
                                  if (contactId) {
                                    setViewingContactId(contactId);
                                    return;
                                  }
                                  setSelectedEntryId(emp._id);
                                }}
                              >
                                {emp.name}
                              </button>
                              {isNewOutput(emp._creationTime, newSince) && (
                                <NewOutputBadge />
                              )}
                              {emp.isExcluded && (
                                <Badge color="gray" variant="soft" size="sm">
                                  Excluded
                                </Badge>
                              )}
                              {relatedByEntry.has(emp._id) ? (
                                <RelatedPartyBadge
                                  match={
                                    relatedByEntry.get(
                                      emp._id,
                                    ) as RelatedPartyMatchUI
                                  }
                                />
                              ) : (
                                emp.isRelatedParty && (
                                  <span
                                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                    title="Related party"
                                  />
                                )
                              )}
                            </div>
                            {emp.role && (
                              <div className="truncate text-[10px] text-muted-foreground">
                                {emp.role}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            {cat ? (
                              <Badge color={cat.color} variant="soft" size="sm">
                                {cat.abbr}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground/40">
                                {"\u2014"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
                            {fmtDate(emp.startDate)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
                            {fmtDate(emp.terminationDate)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(emp.entitlements?.unpaidWages)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(emp.entitlements?.unpaidSuper)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(emp.entitlements?.accruedAnnualLeave)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(emp.entitlements?.leaveLoading)}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(
                              emp.entitlements?.accruedLongServiceLeave,
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm tabular-nums">
                            {fmtCurrency(
                              emp.entitlements?.paymentInLieuOfNotice,
                            )}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-right text-sm font-semibold tabular-nums">
                            {fmtCurrency(total)}
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}

                  {/* Totals row */}
                  {filtered.length > 0 && (
                    <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                      <TableCell
                        className="px-4 py-3 text-sm font-bold text-muted-foreground"
                        colSpan={4}
                      >
                        TOTALS
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.wages)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.super)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.annual)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.loading)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.lsl)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.pilon)}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right text-sm font-bold tabular-nums">
                        {fmtCurrency(totals.total)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Linked names open the contact card · Click the row (or an unlinked
            name) for entitlement details
          </p>
        </div>
      </div>

      {/* Detail panel */}
      <EmployeeDetailPanel
        entry={selectedEntry}
        caseDates={{
          administrationDate:
            job?.administrationStartDate ?? job?.administrationDate,
          liquidationDate: job?.liquidationDate,
          administrationContinuous: job?.administrationContinuous,
          appointmentDate: job?.appointmentDate,
        }}
        open={selectedEntry !== null}
        onClose={() => setSelectedEntryId(null)}
        onDeleted={() => setSelectedEntryId(null)}
        isViewer={isViewer}
      />

      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={jobContacts ?? []}
      />
    </div>
  );
}
